import React, { useState } from 'react'
import {
  Box,
  Container,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  Badge,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  Divider,
  useColorModeValue,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
  useBreakpointValue,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react'
import { 
  CheckCircleIcon, 
  InfoIcon, 
  SettingsIcon,
  ChevronRightIcon,
  TimeIcon,
  StarIcon,
  RepeatIcon,
  ViewIcon,
  ArrowBackIcon,
  HamburgerIcon,
  ChevronUpIcon,
} from '@chakra-ui/icons'
import { useNavigate } from 'react-router-dom'
import CodeEditor from '../components/CodeEditor'

export default function UIRedesignMockup() {
  const navigate = useNavigate()
  const [leftTabIndex, setLeftTabIndex] = useState(0)
  const [rightTabIndex, setRightTabIndex] = useState(0)
  const [toolsConfig, setToolsConfig] = useState({
    correctness: true,
    benchmark: true,
    cilksan: false,
    cilkscale: false,
    profiler: false,
  })
  const [profilerInput, setProfilerInput] = useState('all')
  const [preset, setPreset] = useState('standard')
  
  // Mobile drawer states
  const { isOpen: isLeftDrawerOpen, onOpen: onLeftDrawerOpen, onClose: onLeftDrawerClose } = useDisclosure()
  const { isOpen: isBottomDrawerOpen, onOpen: onBottomDrawerOpen, onClose: onBottomDrawerClose } = useDisclosure()
  
  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false })
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false })
  const leftPanelWidth = useBreakpointValue({ base: '0px', md: '300px', lg: '350px' })
  const rightPanelWidth = useBreakpointValue({ base: '0px', md: '350px', lg: '400px' })
  
  // Sample code
  const [code, setCode] = useState(`#include <iostream>
#include <vector>
#include <cilk/cilk.h>

int parallelSum(std::vector<int>& arr) {
    if (arr.size() <= 1000) {
        int sum = 0;
        for (int val : arr) {
            sum += val;
        }
        return sum;
    }
    
    size_t mid = arr.size() / 2;
    int leftSum, rightSum;
    
    cilk_spawn {
        leftSum = parallelSum(std::vector<int>(arr.begin(), arr.begin() + mid));
    }
    rightSum = parallelSum(std::vector<int>(arr.begin() + mid, arr.end()));
    
    cilk_sync;
    return leftSum + rightSum;
}`)

  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const panelBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const mutedColor = useColorModeValue('gray.600', 'gray.400')

  // Handle preset changes
  const handlePresetChange = (value: string) => {
    setPreset(value)
    switch (value) {
      case 'basic':
        setToolsConfig({
          correctness: true,
          benchmark: false,
          cilksan: false,
          cilkscale: false,
          profiler: false,
        })
        break
      case 'standard':
        setToolsConfig({
          correctness: true,
          benchmark: true,
          cilksan: false,
          cilkscale: false,
          profiler: false,
        })
        break
      case 'full':
        setToolsConfig({
          correctness: true,
          benchmark: true,
          cilksan: true,
          cilkscale: true,
          profiler: true,
        })
        break
    }
  }

  return (
    <Box h="100vh" bg={bgColor} overflow="hidden">
      {/* Navigation Bar */}
      <Flex
        h="48px"
        bg={panelBg}
        borderBottom="1px"
        borderColor={borderColor}
        align="center"
        px={4}
        shadow="sm"
      >
        <HStack spacing={2} flex={1}>
          {isMobile && (
            <IconButton
              aria-label="Menu"
              icon={<HamburgerIcon />}
              variant="ghost"
              size="sm"
              onClick={onLeftDrawerOpen}
            />
          )}
          <IconButton
            aria-label="Back"
            icon={<ArrowBackIcon />}
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
          />
          <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={textColor}>
            Speedcode
          </Text>
          <Badge colorScheme="purple" fontSize="xs" display={{ base: 'none', md: 'block' }}>
            UI Redesign Mockup
          </Badge>
          <Text color={mutedColor} fontSize="sm" display={{ base: 'none', md: 'block' }}>
            Problem: Two Sum
          </Text>
        </HStack>
        <HStack spacing={2}>
          <Button 
            colorScheme="green" 
            size="sm"
            leftIcon={!isMobile ? <CheckCircleIcon /> : undefined}
          >
            {isMobile ? 'Submit' : 'Submit'}
          </Button>
          {!isMobile && (
            <IconButton
              aria-label="Settings"
              icon={<SettingsIcon />}
              variant="ghost"
              size="sm"
            />
          )}
        </HStack>
      </Flex>

      {/* Main Content Area */}
      <Flex h="calc(100vh - 48px)" overflow="hidden" position="relative">
        {/* Left Panel - Desktop Only */}
        <Box
          w={leftPanelWidth}
          bg={panelBg}
          borderRight="1px"
          borderColor={borderColor}
          overflow="hidden"
          display={{ base: 'none', md: 'flex' }}
          flexDirection="column"
        >
          <Tabs 
            index={leftTabIndex} 
            onChange={setLeftTabIndex}
            variant="enclosed"
            h="100%"
            display="flex"
            flexDirection="column"
          >
            <TabList borderBottom="1px" borderColor={borderColor}>
              <Tab fontSize="sm">
                <Icon as={InfoIcon} mr={2} />
                Description
              </Tab>
              <Tab fontSize="sm">
                <Icon as={SettingsIcon} mr={2} />
                Tools & Config
              </Tab>
              <Tab fontSize="sm">
                <Icon as={TimeIcon} mr={2} />
                History
              </Tab>
            </TabList>

            <TabPanels flex={1} overflow="hidden">
              {/* Description Tab */}
              <TabPanel h="100%" overflow="auto" p={4}>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontSize="2xl" fontWeight="bold" mb={2}>Two Sum</Text>
                    <Badge colorScheme="green" mb={4}>Easy</Badge>
                  </Box>
                  
                  <Text>
                    Given an array of integers nums and an integer target, return indices of the two numbers such that they add up to target.
                  </Text>
                  
                  <Text>
                    You may assume that each input would have exactly one solution, and you may not use the same element twice.
                  </Text>
                  
                  <Box>
                    <Text fontWeight="bold" mb={2}>Example:</Text>
                    <Box bg="gray.100" p={3} borderRadius="md">
                      <Text fontFamily="mono" fontSize="sm">
                        Input: nums = [2,7,11,15], target = 9{'\n'}
                        Output: [0,1]{'\n'}
                        Explanation: nums[0] + nums[1] == 9
                      </Text>
                    </Box>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="bold" mb={2}>Performance Goal:</Text>
                    <Text>
                      Optimize your solution to handle large arrays efficiently. Consider parallelization strategies for arrays with millions of elements.
                    </Text>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Tools & Config Tab */}
              <TabPanel h="100%" overflow="auto" p={4}>
                <VStack align="stretch" spacing={6}>
                  {/* Test Selection */}
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" mb={3} color={textColor}>
                      🎯 Test Configuration
                    </Text>
                    <VStack align="stretch" spacing={2} pl={4}>
                      <Checkbox 
                        isChecked={toolsConfig.correctness}
                        isDisabled
                        colorScheme="green"
                      >
                        <Text fontSize="sm">Correctness Tests</Text>
                        <Text fontSize="xs" color={mutedColor}>Always enabled</Text>
                      </Checkbox>
                      
                      <Checkbox 
                        isChecked={toolsConfig.benchmark}
                        onChange={(e) => setToolsConfig({...toolsConfig, benchmark: e.target.checked})}
                        colorScheme="blue"
                      >
                        <Text fontSize="sm">Performance Benchmark</Text>
                        <Text fontSize="xs" color={mutedColor}>Compare against reference</Text>
                      </Checkbox>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Advanced Analysis */}
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" mb={3} color={textColor}>
                      🔬 Advanced Analysis Tools
                    </Text>
                    <VStack align="stretch" spacing={2} pl={4}>
                      <Checkbox 
                        isChecked={toolsConfig.cilksan}
                        onChange={(e) => setToolsConfig({...toolsConfig, cilksan: e.target.checked})}
                        colorScheme="orange"
                      >
                        <Text fontSize="sm">Race Detection (Cilksan)</Text>
                        <Text fontSize="xs" color={mutedColor}>Find data races in parallel code</Text>
                      </Checkbox>
                      
                      <Checkbox 
                        isChecked={toolsConfig.cilkscale}
                        onChange={(e) => setToolsConfig({...toolsConfig, cilkscale: e.target.checked})}
                        colorScheme="teal"
                      >
                        <Text fontSize="sm">Scalability Analysis (Cilkscale)</Text>
                        <Text fontSize="xs" color={mutedColor}>Measure parallelism and efficiency</Text>
                      </Checkbox>
                      
                      <Checkbox 
                        isChecked={toolsConfig.profiler}
                        onChange={(e) => setToolsConfig({...toolsConfig, profiler: e.target.checked})}
                        colorScheme="purple"
                      >
                        <Text fontSize="sm">Line-by-Line Profiler</Text>
                        <Text fontSize="xs" color={mutedColor}>Identify performance hotspots</Text>
                      </Checkbox>
                    </VStack>
                  </Box>

                  {/* Profiler Settings */}
                  {toolsConfig.profiler && (
                    <>
                      <Divider />
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" mb={3} color={textColor}>
                          ⚙️ Profiler Settings
                        </Text>
                        <VStack align="stretch" spacing={3} pl={4}>
                          <Box>
                            <Text fontSize="sm" mb={2}>Input Selection:</Text>
                            <RadioGroup value={profilerInput} onChange={setProfilerInput}>
                              <VStack align="stretch" spacing={2}>
                                <Radio value="all" size="sm">All inputs</Radio>
                                <Radio value="specific" size="sm">Specific input</Radio>
                              </VStack>
                            </RadioGroup>
                            {profilerInput === 'specific' && (
                              <Select size="sm" mt={2} placeholder="Select input">
                                <option value="input_100.txt">input_100.txt</option>
                                <option value="input_1000.txt">input_1000.txt</option>
                                <option value="input_10000.txt">input_10000.txt</option>
                              </Select>
                            )}
                          </Box>
                          <Alert status="info" fontSize="xs">
                            <AlertIcon />
                            When a specific input is selected, ALL tests will run only on that input.
                          </Alert>
                        </VStack>
                      </Box>
                    </>
                  )}

                  <Divider />

                  {/* Quick Presets */}
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" mb={3} color={textColor}>
                      ⚡ Quick Presets
                    </Text>
                    <RadioGroup value={preset} onChange={handlePresetChange}>
                      <VStack align="stretch" spacing={2} pl={4}>
                        <Radio value="basic" colorScheme="gray">
                          <Text fontSize="sm">Basic</Text>
                          <Text fontSize="xs" color={mutedColor}>Correctness only</Text>
                        </Radio>
                        <Radio value="standard" colorScheme="blue">
                          <Text fontSize="sm">Standard</Text>
                          <Text fontSize="xs" color={mutedColor}>+ Performance benchmark</Text>
                        </Radio>
                        <Radio value="full" colorScheme="purple">
                          <Text fontSize="sm">Full Analysis</Text>
                          <Text fontSize="xs" color={mutedColor}>All tools enabled</Text>
                        </Radio>
                      </VStack>
                    </RadioGroup>
                  </Box>
                </VStack>
              </TabPanel>

              {/* History Tab */}
              <TabPanel h="100%" overflow="auto" p={4}>
                <VStack align="stretch" spacing={3}>
                  <Text fontWeight="bold" fontSize="sm" color={textColor}>
                    Submission History
                  </Text>
                  
                  {/* Mock history entries */}
                  {[
                    { time: '2 minutes ago', status: 'success', speedup: '2.34x' },
                    { time: '15 minutes ago', status: 'fail', speedup: 'N/A' },
                    { time: '1 hour ago', status: 'success', speedup: '1.89x' },
                  ].map((entry, idx) => (
                    <Box
                      key={idx}
                      p={3}
                      borderWidth={1}
                      borderColor={borderColor}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {entry.time}
                          </Text>
                          <Badge 
                            colorScheme={entry.status === 'success' ? 'green' : 'red'}
                            fontSize="xs"
                          >
                            {entry.status === 'success' ? 'Passed' : 'Failed'}
                          </Badge>
                        </VStack>
                        {entry.speedup !== 'N/A' && (
                          <Text fontSize="sm" color={mutedColor}>
                            {entry.speedup}
                          </Text>
                        )}
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        {/* Center - Code Editor */}
        <Box flex={1} display="flex" flexDirection="column" minW="400px">
          {/* Editor Toolbar */}
          <HStack
            h="40px"
            px={4}
            bg={panelBg}
            borderBottom="1px"
            borderColor={borderColor}
            justify="space-between"
          >
            <HStack spacing={3}>
              <Text fontSize="sm" fontWeight="medium" color={textColor}>
                Code Editor
              </Text>
              <Badge colorScheme="blue" fontSize="xs">C++</Badge>
            </HStack>
            <HStack spacing={2}>
              <Select size="xs" defaultValue="default" w="100px">
                <option value="default">Default</option>
                <option value="vim">Vim</option>
                <option value="emacs">Emacs</option>
              </Select>
              <Button size="xs" variant="ghost">Format</Button>
            </HStack>
          </HStack>
          
          {/* Code Editor */}
          <Box flex={1}>
            <CodeEditor
              value={code}
              onChange={setCode}
              language="cpp"
              height="100%"
            />
          </Box>
        </Box>

        {/* Right Panel - Results - Desktop Only */}
        <Box
          w={rightPanelWidth}
          bg={panelBg}
          borderLeft="1px"
          borderColor={borderColor}
          overflow="hidden"
          display={{ base: 'none', md: 'flex' }}
          flexDirection="column"
        >
          <Tabs 
            index={rightTabIndex} 
            onChange={setRightTabIndex}
            variant="enclosed"
            h="100%"
            display="flex"
            flexDirection="column"
          >
            <TabList borderBottom="1px" borderColor={borderColor}>
              <Tab fontSize="sm" flex={1}>Overview</Tab>
              <Tab fontSize="sm" flex={1}>Performance</Tab>
              <Tab fontSize="sm" flex={1}>Analysis</Tab>
              <Tab fontSize="sm" flex={1}>Output</Tab>
            </TabList>

            <TabPanels flex={1} overflow="hidden">
              {/* Overview Tab */}
              <TabPanel h="100%" overflow="auto" p={4}>
                <VStack align="stretch" spacing={4}>
                  {/* Overall Status */}
                  <Box
                    p={4}
                    bg="green.50"
                    borderRadius="md"
                    borderWidth={1}
                    borderColor="green.200"
                  >
                    <HStack>
                      <Icon as={CheckCircleIcon} color="green.500" boxSize={6} />
                      <Text fontWeight="bold" color="green.800">
                        All Tests Passed
                      </Text>
                    </HStack>
                  </Box>

                  {/* Summary Cards */}
                  <VStack spacing={3}>
                    {/* Correctness Card */}
                    <Box
                      p={4}
                      borderWidth={1}
                      borderColor={borderColor}
                      borderRadius="md"
                      w="100%"
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                      onClick={() => setRightTabIndex(3)}
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <HStack>
                            <Icon as={CheckCircleIcon} color="green.500" />
                            <Text fontWeight="medium">Correctness</Text>
                          </HStack>
                          <Text fontSize="2xl" fontWeight="bold" color="green.600">
                            10/10
                          </Text>
                          <Text fontSize="sm" color={mutedColor}>
                            All tests passed
                          </Text>
                        </VStack>
                        <Icon as={ChevronRightIcon} color={mutedColor} />
                      </HStack>
                    </Box>

                    {/* Performance Card */}
                    <Box
                      p={4}
                      borderWidth={1}
                      borderColor={borderColor}
                      borderRadius="md"
                      w="100%"
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                      onClick={() => setRightTabIndex(1)}
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <HStack>
                            <Icon as={StarIcon} color="yellow.500" />
                            <Text fontWeight="medium">Performance</Text>
                          </HStack>
                          <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                            2.34x
                          </Text>
                          <Text fontSize="sm" color={mutedColor}>
                            Speedup vs reference
                          </Text>
                          <Badge colorScheme="purple" fontSize="xs">
                            Rank #3 of 15
                          </Badge>
                        </VStack>
                        <Icon as={ChevronRightIcon} color={mutedColor} />
                      </HStack>
                    </Box>

                    {/* Code Quality Card */}
                    <Box
                      p={4}
                      borderWidth={1}
                      borderColor={borderColor}
                      borderRadius="md"
                      w="100%"
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                      onClick={() => setRightTabIndex(2)}
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <HStack>
                            <Icon as={RepeatIcon} color="teal.500" />
                            <Text fontWeight="medium">Code Quality</Text>
                          </HStack>
                          <VStack align="start" spacing={2} mt={2}>
                            <HStack>
                              <Badge colorScheme="green" fontSize="xs">✓</Badge>
                              <Text fontSize="sm">No races detected</Text>
                            </HStack>
                            <HStack>
                              <Badge colorScheme="blue" fontSize="xs">87%</Badge>
                              <Text fontSize="sm">Parallelism efficiency</Text>
                            </HStack>
                          </VStack>
                        </VStack>
                        <Icon as={ChevronRightIcon} color={mutedColor} />
                      </HStack>
                    </Box>
                  </VStack>

                  {/* Quick Actions */}
                  <Box pt={4}>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      w="100%"
                      leftIcon={<ViewIcon />}
                    >
                      View Detailed Report
                    </Button>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Performance Tab */}
              <TabPanel h="100%" overflow="auto" p={4}>
                <VStack align="stretch" spacing={4}>
                  <Text fontWeight="bold" fontSize="lg">Performance Analysis</Text>
                  
                  {/* Mock Chart Area */}
                  <Box
                    h="200px"
                    bg="gray.50"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderWidth={1}
                    borderColor={borderColor}
                  >
                    <Text color={mutedColor}>Speedup Comparison Chart</Text>
                  </Box>

                  {/* Performance Table */}
                  <Box>
                    <Text fontWeight="medium" fontSize="sm" mb={2}>
                      Detailed Results
                    </Text>
                    <TableContainer>
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Implementation</Th>
                            <Th isNumeric>Runtime</Th>
                            <Th isNumeric>Speedup</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td>Reference</Td>
                            <Td isNumeric>1.234s</Td>
                            <Td isNumeric>1.00x</Td>
                          </Tr>
                          <Tr bg="green.50">
                            <Td fontWeight="bold">Your Code</Td>
                            <Td isNumeric fontWeight="bold">0.527s</Td>
                            <Td isNumeric fontWeight="bold" color="green.600">2.34x</Td>
                          </Tr>
                          <Tr>
                            <Td>Best Solution</Td>
                            <Td isNumeric>0.423s</Td>
                            <Td isNumeric color="blue.600">2.92x</Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Key Metrics */}
                  <Box>
                    <Text fontWeight="medium" fontSize="sm" mb={2}>
                      Key Metrics
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between">
                        <Text fontSize="sm">Instructions executed:</Text>
                        <Text fontSize="sm" fontFamily="mono">1,234,567</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm">Cache misses:</Text>
                        <Text fontSize="sm" fontFamily="mono">423</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm">Branch mispredicts:</Text>
                        <Text fontSize="sm" fontFamily="mono">89</Text>
                      </HStack>
                    </VStack>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Analysis Tab */}
              <TabPanel h="100%" overflow="auto" p={0}>
                <Tabs variant="enclosed" size="sm">
                  <TabList px={4} pt={2}>
                    <Tab>Race Detection</Tab>
                    <Tab>Scalability</Tab>
                    <Tab>Profiler</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="success" borderRadius="md">
                          <AlertIcon />
                          <Text fontSize="sm">No data races detected!</Text>
                        </Alert>
                        <Text fontSize="sm" color={mutedColor}>
                          Cilksan analysis completed successfully with 0 races found in your parallel code.
                        </Text>
                      </VStack>
                    </TabPanel>
                    <TabPanel>
                      <VStack align="stretch" spacing={3}>
                        <Stat>
                          <StatLabel>Measured Parallelism</StatLabel>
                          <StatNumber>87%</StatNumber>
                          <StatHelpText>
                            <Progress value={87} size="sm" colorScheme="blue" />
                          </StatHelpText>
                        </Stat>
                        <Text fontSize="sm" color={mutedColor}>
                          Your code achieves good parallel efficiency with minimal overhead.
                        </Text>
                      </VStack>
                    </TabPanel>
                    <TabPanel>
                      <VStack align="stretch" spacing={3}>
                        <Text fontSize="sm" fontWeight="medium">
                          Performance Hotspots:
                        </Text>
                        <Box
                          p={2}
                          bg="red.50"
                          borderRadius="md"
                          borderWidth={1}
                          borderColor="red.200"
                        >
                          <Text fontSize="xs" fontFamily="mono">
                            Line 15: 45% of execution time
                          </Text>
                        </Box>
                        <Box
                          p={2}
                          bg="orange.50"
                          borderRadius="md"
                          borderWidth={1}
                          borderColor="orange.200"
                        >
                          <Text fontSize="xs" fontFamily="mono">
                            Line 8: 23% of execution time
                          </Text>
                        </Box>
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </TabPanel>

              {/* Output Tab */}
              <TabPanel h="100%" overflow="auto" p={4}>
                <VStack align="stretch" spacing={4}>
                  <Text fontWeight="bold" fontSize="sm">Program Output</Text>
                  <Box
                    p={3}
                    bg="gray.900"
                    color="green.300"
                    fontFamily="mono"
                    fontSize="xs"
                    borderRadius="md"
                    overflowX="auto"
                  >
                    <Text>Running correctness tests...</Text>
                    <Text>Test 1: PASSED ✓</Text>
                    <Text>Test 2: PASSED ✓</Text>
                    <Text>Test 3: PASSED ✓</Text>
                    <Text>...</Text>
                    <Text>All tests passed! 10/10</Text>
                    <Text mt={2}>Running performance benchmark...</Text>
                    <Text>Reference time: 1.234s</Text>
                    <Text>Your time: 0.527s</Text>
                    <Text>Speedup: 2.34x</Text>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
        
        {/* Mobile Bottom Action Button */}
        {isMobile && (
          <Box
            position="fixed"
            bottom={4}
            right={4}
            zIndex={10}
          >
            <Button
              colorScheme="blue"
              size="lg"
              borderRadius="full"
              shadow="lg"
              leftIcon={<ChevronUpIcon />}
              onClick={onBottomDrawerOpen}
            >
              Results
            </Button>
          </Box>
        )}
      </Flex>

      {/* Mobile Left Drawer */}
      <Drawer
        isOpen={isLeftDrawerOpen}
        placement="left"
        onClose={onLeftDrawerClose}
        size="sm"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Text fontSize="lg" fontWeight="bold">Speedcode</Text>
            <Text fontSize="sm" color={mutedColor}>Problem: Two Sum</Text>
          </DrawerHeader>
          <DrawerBody p={0}>
            <Tabs 
              index={leftTabIndex} 
              onChange={setLeftTabIndex}
              variant="enclosed"
              h="100%"
            >
              <TabList borderBottom="1px" borderColor={borderColor}>
                <Tab fontSize="sm" flex={1}>
                  <Icon as={InfoIcon} mr={2} />
                  Description
                </Tab>
                <Tab fontSize="sm" flex={1}>
                  <Icon as={SettingsIcon} mr={2} />
                  Tools
                </Tab>
                <Tab fontSize="sm" flex={1}>
                  <Icon as={TimeIcon} mr={2} />
                  History
                </Tab>
              </TabList>

              <TabPanels>
                {/* Mobile Description Tab - Same content as desktop */}
                <TabPanel h="100%" overflow="auto" p={4}>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontSize="2xl" fontWeight="bold" mb={2}>Two Sum</Text>
                      <Badge colorScheme="green" mb={4}>Easy</Badge>
                    </Box>
                    
                    <Text>
                      Given an array of integers nums and an integer target, return indices of the two numbers such that they add up to target.
                    </Text>
                    
                    <Text>
                      You may assume that each input would have exactly one solution, and you may not use the same element twice.
                    </Text>
                    
                    <Box>
                      <Text fontWeight="bold" mb={2}>Example:</Text>
                      <Box bg="gray.100" p={3} borderRadius="md">
                        <Text fontFamily="mono" fontSize="sm">
                          Input: nums = [2,7,11,15], target = 9{'\n'}
                          Output: [0,1]{'\n'}
                          Explanation: nums[0] + nums[1] == 9
                        </Text>
                      </Box>
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Mobile Tools Tab - Same content structure */}
                <TabPanel h="100%" overflow="auto" p={4}>
                  <VStack align="stretch" spacing={6}>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" mb={3} color={textColor}>
                        🎯 Test Configuration
                      </Text>
                      <VStack align="stretch" spacing={2} pl={4}>
                        <Checkbox isChecked={toolsConfig.correctness} isDisabled colorScheme="green">
                          <Text fontSize="sm">Correctness Tests</Text>
                        </Checkbox>
                        <Checkbox isChecked={toolsConfig.benchmark} onChange={(e) => setToolsConfig({...toolsConfig, benchmark: e.target.checked})} colorScheme="blue">
                          <Text fontSize="sm">Performance Benchmark</Text>
                        </Checkbox>
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Mobile History Tab */}
                <TabPanel h="100%" overflow="auto" p={4}>
                  <VStack align="stretch" spacing={3}>
                    <Text fontWeight="bold" fontSize="sm" color={textColor}>
                      Submission History
                    </Text>
                    {[
                      { time: '2 minutes ago', status: 'success', speedup: '2.34x' },
                      { time: '15 minutes ago', status: 'fail', speedup: 'N/A' },
                    ].map((entry, idx) => (
                      <Box key={idx} p={3} borderWidth={1} borderColor={borderColor} borderRadius="md">
                        <HStack justify="space-between">
                          <Text fontSize="sm">{entry.time}</Text>
                          <Badge colorScheme={entry.status === 'success' ? 'green' : 'red'} fontSize="xs">
                            {entry.status === 'success' ? 'Passed' : 'Failed'}
                          </Badge>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Mobile Bottom Sheet for Results */}
      <Drawer
        isOpen={isBottomDrawerOpen}
        placement="bottom"
        onClose={onBottomDrawerClose}
        size="full"
      >
        <DrawerOverlay />
        <DrawerContent maxH="90vh">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Text fontSize="lg" fontWeight="bold">Results</Text>
          </DrawerHeader>
          <DrawerBody p={0}>
            <Tabs 
              index={rightTabIndex} 
              onChange={setRightTabIndex}
              variant="enclosed"
              h="100%"
            >
              <TabList borderBottom="1px" borderColor={borderColor}>
                <Tab fontSize="sm" flex={1}>Overview</Tab>
                <Tab fontSize="sm" flex={1}>Performance</Tab>
                <Tab fontSize="sm" flex={1}>Analysis</Tab>
                <Tab fontSize="sm" flex={1}>Output</Tab>
              </TabList>

              <TabPanels>
                {/* Mobile Overview Tab - Simplified */}
                <TabPanel h="100%" overflow="auto" p={4}>
                  <VStack align="stretch" spacing={4}>
                    <Box p={4} bg="green.50" borderRadius="md" borderWidth={1} borderColor="green.200">
                      <HStack>
                        <Icon as={CheckCircleIcon} color="green.500" boxSize={6} />
                        <Text fontWeight="bold" color="green.800">All Tests Passed</Text>
                      </HStack>
                    </Box>

                    <VStack spacing={3}>
                      <Box p={4} borderWidth={1} borderColor={borderColor} borderRadius="md" w="100%">
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">Correctness</Text>
                            <Text fontSize="2xl" fontWeight="bold" color="green.600">10/10</Text>
                          </VStack>
                        </HStack>
                      </Box>

                      <Box p={4} borderWidth={1} borderColor={borderColor} borderRadius="md" w="100%">
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">Performance</Text>
                            <Text fontSize="2xl" fontWeight="bold" color="blue.600">2.34x</Text>
                            <Badge colorScheme="purple" fontSize="xs">Rank #3</Badge>
                          </VStack>
                        </HStack>
                      </Box>
                    </VStack>
                  </VStack>
                </TabPanel>

                {/* Other mobile tabs with simplified content */}
                <TabPanel>
                  <Text p={4}>Performance details (mobile optimized)</Text>
                </TabPanel>
                <TabPanel>
                  <Text p={4}>Analysis results (mobile optimized)</Text>
                </TabPanel>
                <TabPanel>
                  <Text p={4}>Output logs (mobile optimized)</Text>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  )
}
